import { useRef, useState } from "react";
import MDEditor from "@uiw/react-md-editor";
import { Eye, PenLine, Image as ImageIcon, Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import MarkdownView from "@/components/MarkdownView";

/**
 * MarkdownEditor with live preview and image upload that injects markdown image syntax.
 * Props: value, onChange, height
 */
export default function MarkdownEditor({ value = "", onChange, height = 360, testIdPrefix = "md" }) {
  const [mode, setMode] = useState("edit"); // edit | preview
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const insertAt = (snippet) => {
    const newVal = (value || "") + (value && !value.endsWith("\n") ? "\n\n" : "") + snippet + "\n";
    onChange(newVal);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fullUrl = await adminUpload(file);
      insertAt(`![${file.name}](${fullUrl})`);
      toast.success("Image inserted");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="rounded-xl border border-sand bg-white" data-testid={`${testIdPrefix}-editor`}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-sand bg-sand/30 rounded-t-xl">
        <div className="inline-flex rounded-full bg-white border border-sand p-0.5">
          <button
            type="button"
            onClick={() => setMode("edit")}
            data-testid={`${testIdPrefix}-mode-edit`}
            className={`px-3 py-1 text-xs font-semibold rounded-full inline-flex items-center gap-1.5 transition ${mode === "edit" ? "bg-navy text-cream" : "text-ink/60 hover:text-navy"}`}
          >
            <PenLine size={11} /> Edit
          </button>
          <button
            type="button"
            onClick={() => setMode("preview")}
            data-testid={`${testIdPrefix}-mode-preview`}
            className={`px-3 py-1 text-xs font-semibold rounded-full inline-flex items-center gap-1.5 transition ${mode === "preview" ? "bg-navy text-cream" : "text-ink/60 hover:text-navy"}`}
          >
            <Eye size={11} /> Preview
          </button>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
            data-testid={`${testIdPrefix}-image-input`}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testIdPrefix}-image-btn`}
            className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 text-navy hover:bg-gold/25 px-3 py-1 text-xs font-semibold disabled:opacity-60"
          >
            {uploading ? <Loader2 size={11} className="animate-spin" /> : <ImageIcon size={11} />}
            {uploading ? "Uploading…" : "Add image"}
          </button>
        </div>
      </div>

      {mode === "edit" ? (
        <div data-color-mode="light" className="rounded-b-xl overflow-hidden">
          <MDEditor
            value={value || ""}
            onChange={(v) => onChange(v ?? "")}
            preview="edit"
            visibleDragbar={false}
            height={height}
            textareaProps={{ "data-testid": `${testIdPrefix}-textarea` }}
          />
        </div>
      ) : (
        <div className="p-5 min-h-[280px] max-h-[480px] overflow-y-auto" data-testid={`${testIdPrefix}-preview-pane`}>
          {value ? <MarkdownView>{value}</MarkdownView> : <div className="text-sm text-ink/50 italic">Nothing to preview yet — switch back to Edit and start typing.</div>}
        </div>
      )}

      <div className="px-3 py-2 border-t border-sand text-[10px] text-ink/50 leading-relaxed">
        Markdown supported: <code className="bg-sand/60 px-1 rounded">**bold**</code>, <code className="bg-sand/60 px-1 rounded">*italic*</code>, <code className="bg-sand/60 px-1 rounded">[link](url)</code>, <code className="bg-sand/60 px-1 rounded">## heading</code>, <code className="bg-sand/60 px-1 rounded">- list</code>, <code className="bg-sand/60 px-1 rounded">{">"} quote</code>. Use the "Add image" button to upload + embed.
      </div>
    </div>
  );
}
